import axios from 'axios';
import { baseUrl } from '../Infrastructure/Config/ApiConfig';
import { token } from './AuthService';

const controllerName = 'Course';
const moduleUploadChunkSize = 4 * 1024 * 1024;

const getResponseData = (response) => response?.data?.entity ?? response?.data;

const toNullableNumber = (value) => {
    if (value === undefined || value === null || value === '') {
        return null;
    }

    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : null;
};

const toBoolean = (value) => {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'string') {
        return value.toLowerCase() === 'true';
    }

    return Boolean(value);
};

const buildBlobUrl = (sasUrl, query) => `${sasUrl}${sasUrl.includes('?') ? '&' : '?'}${query}`;

const getBlobUploadDiagnostics = (sasUrl) => {
    try {
        const parsedUrl = new URL(sasUrl);
        const permissions = parsedUrl.searchParams.get('sp') || '';

        return {
            storageOrigin: parsedUrl.origin,
            blobPath: parsedUrl.pathname,
            permissions,
            hasSignature: parsedUrl.searchParams.has('sig'),
            hasWritePermissions: permissions.includes('w') || permissions.includes('c'),
            appOrigin: typeof window !== 'undefined' ? window.location.origin : 'unknown'
        };
    } catch {
        return null;
    }
};

const performBlobRequestAsync = async (operation, url, options, diagnostics) => {
    try {
        const response = await fetch(url, options);

        if (!response.ok) {
            const errorText = await response.text().catch(() => '');
            console.error(`${operation} failed`, {
                url,
                status: response.status,
                statusText: response.statusText,
                diagnostics,
                errorText
            });

            throw new Error(`${operation} failed with status ${response.status}. ${errorText || response.statusText}`.trim());
        }

        return response;
    } catch (error) {
        if (error?.name === 'TypeError') {
            console.error(`${operation} was blocked before Azure returned a response`, {
                url,
                diagnostics,
                originalMessage: error?.message
            });

            throw new Error(
                `Direct upload to blob storage was blocked before a response was returned. ` +
                `This usually indicates Azure Storage CORS is missing for ${diagnostics?.appOrigin || 'this app'} ` +
                `or the SAS token does not allow the required upload operation.`
            );
        }

        throw error;
    }
};

const uploadModuleFileToBlobAsync = async (file, sasUrl, onUploadProgress) => {
    const totalBytes = file?.size || 0;
    const blockIds = [];
    const diagnostics = getBlobUploadDiagnostics(sasUrl);

    if (!diagnostics?.hasSignature) {
        throw new Error('The SAS URL is invalid or missing its signature.');
    }

    if (!diagnostics?.hasWritePermissions) {
        throw new Error(`The SAS token is missing blob write/create permissions. Permissions received: ${diagnostics?.permissions || 'none'}`);
    }

    for (let offset = 0; offset < totalBytes; offset += moduleUploadChunkSize) {
        const chunk = file.slice(offset, Math.min(offset + moduleUploadChunkSize, totalBytes));
        const blockId = btoa(`block-${String(offset).padStart(12, '0')}`);
        const blockUrl = buildBlobUrl(sasUrl, `comp=block&blockid=${encodeURIComponent(blockId)}`);

        blockIds.push(blockId);

        await performBlobRequestAsync('Uploading module chunk to Azure Blob Storage', blockUrl, {
            method: 'PUT',
            headers: {
                'x-ms-version': '2021-12-02'
            },
            body: chunk
        }, diagnostics);

        if (typeof onUploadProgress === 'function') {
            onUploadProgress({
                loaded: Math.min(offset + chunk.size, totalBytes),
                total: totalBytes,
                phase: 'uploading'
            });
        }
    }

    if (typeof onUploadProgress === 'function') {
        onUploadProgress({
            loaded: totalBytes,
            total: totalBytes,
            phase: 'processing'
        });
    }

    const commitUrl = buildBlobUrl(sasUrl, 'comp=blocklist');
    const blockListXml = `<?xml version="1.0" encoding="utf-8"?><BlockList>${blockIds.map(id => `<Latest>${id}</Latest>`).join('')}</BlockList>`;

    await performBlobRequestAsync('Committing uploaded module in Azure Blob Storage', commitUrl, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/xml',
            'x-ms-version': '2021-12-02'
        },
        body: blockListXml
    }, diagnostics);

    if (typeof onUploadProgress === 'function') {
        onUploadProgress({
            loaded: totalBytes,
            total: totalBytes,
            phase: 'complete'
        });
    }
};

export const uploadCourseAsync = async (data) => {
    const response = await axios.post(`${baseUrl()}${controllerName}/UploadCourse`, data, {
        headers: {
            'Authorization': `Bearer ${token()}`
        }
    });
    return response.data;
}

export const updateCourseAsync = async (data) => {
    const response = await axios.post(`${baseUrl()}${controllerName}/UpdateCourse`, data, {
        headers: {
            'Authorization': `Bearer ${token()}`
        }
    });
    return response.data;
}

export const populateCourseDropdownAsync = async (pathwayId) => {
    let qry = '';
    if (pathwayId !== undefined) {
        qry = '?pathwayId=' + pathwayId
    }
    const response = await axios.get(`${baseUrl()}${controllerName}/populateCourseDropdown${qry}`, {
        headers: {
            'Authorization': `Bearer ${token()}`
        }
    });
    return response.data;
}

export const createPathwayAsync = async (data) => {
    const response = await axios.post(`${baseUrl()}${controllerName}/CreatePathway`, data, {
        headers: {
            'Authorization': `Bearer ${token()}`
        }
    });
    return response.data;
}

export const updatePathwayAsync = async (data) => {
    const response = await axios.post(`${baseUrl()}${controllerName}/UpdateLearningPathway`, data, {
        headers: {
            'Authorization': `Bearer ${token()}`
        }
    });
    return response.data;
}

export const getLearningPathwaysAsync = async () => {
    const response = await axios.get(`${baseUrl()}${controllerName}/GetAllLearningPathways`, {
        headers: {
            'Authorization': `Bearer ${token()}`
        }
    });
    return response.data;
}

export const getClientLearningPathwaysAsync = async () => {
    const response = await axios.get(`${baseUrl()}${controllerName}/GetAllClientLearningPathways`, {
        headers: {
            'Authorization': `Bearer ${token()}`
        }
    });
    return response.data;
}

export const addCoursesToPathwayAsync = async (data) => {
    const response = await axios.post(`${baseUrl()}${controllerName}/AddCourseToPathway`, data, {
        headers: {
            'Authorization': `Bearer ${token()}`
        }
    });
    return response.data;
}

export const getLearningPathwayAsync = async (id) => {
    const response = await axios.get(`${baseUrl()}${controllerName}/GetLearningPathway/${id}`, {
        headers: {
            'Authorization': `Bearer ${token()}`
        }
    });
    return response.data;
}

export const assignClientToPathwayAsync = async (data) => {
    const response = await axios.post(`${baseUrl()}${controllerName}/AssignPathwayToClient`, data, {
        headers: {
            'Authorization': `Bearer ${token()}`
        }
    });
    return response.data;
}

export const removePathwayFromClientAsync = async (data) => {
    const response = await axios.post(`${baseUrl()}${controllerName}/RemovePathwayFromClient`, data, {
        headers: {
            'Authorization': `Bearer ${token()}`
        }
    });
    return response.data;
}

export const assignCourseToClientAsync = async (data) => {
    const response = await axios.post(`${baseUrl()}${controllerName}/AssignCourseToClient`, data, {
        headers: {
            'Authorization': `Bearer ${token()}`
        }
    });
    return response.data;
}

export const removeCourseFromClientAsync = async (data) => {
    const response = await axios.post(`${baseUrl()}${controllerName}/RemoveCourseFromClient`, data, {
        headers: {
            'Authorization': `Bearer ${token()}`
        }
    });
    return response.data;
}

export const getAssignedCoursesForClientAsync = async (clientId) => {
    const response = await axios.get(`${baseUrl()}${controllerName}/GetAssignedCoursesForClient/${clientId}`, {
        headers: {
            'Authorization': `Bearer ${token()}`
        }
    });
    return response.data;
}

export const getAllCoursesAsync = async (pageNumber) => {
    const response = await axios.get(`${baseUrl()}${controllerName}/GetAllCourses/${pageNumber}`, {
        headers: {
            'Authorization': `Bearer ${token()}`
        }
    });
    return response.data;
}

export const getCourseByIdAsync = async (courseId) => {
    const response = await axios.get(`${baseUrl()}${controllerName}/getCourseById/${courseId}`, {
        headers: {
            'Authorization': `Bearer ${token()}`
        }
    });
    return response.data;
}

export const getLearnerCourseAsync = async (courseId) => {
    const response = await axios.get(`${baseUrl()}${controllerName}/GetLearnerCourse/${courseId}`, {
        headers: {
            'Authorization': `Bearer ${token()}`
        }
    });
    return response.data;
}

export const getCourseProgressAsync = async (courseId, clientId, pageNumber) => {
    const response = await axios.get(`${baseUrl()}${controllerName}/GetCourseProgress?courseId=${courseId}&pageNumber=${pageNumber}&clientId=${clientId}`, {
        headers: {
            'Authorization': `Bearer ${token()}`
        }
    });

    return response.data;
}

export const getClientCoursesAsync = async () => {
    const response = await axios.get(`${baseUrl()}${controllerName}/GetClientCourses`, {
        headers: {
            'Authorization': `Bearer ${token()}`
        }
    });
    return response.data;
}

export const getModuleTypesAsync = async () => {
    const response = await axios.get(`${baseUrl()}${controllerName}/ModuleTypes`, {
        headers: {
            'Authorization': `Bearer ${token()}`
        }
    });
    return response.data;
}

export const getClientProgressCoursesAsync = async (clientId) => {
    const response = await axios.get(`${baseUrl()}${controllerName}/GetClientProgressCourses/${clientId}`, {
        headers: {
            'Authorization': `Bearer ${token()}`
        }
    });
    return response.data;
}

export const restartCourseAsync = async (data) => {
    const response = await axios.post(`${baseUrl()}${controllerName}/ResetCourseProgress`, data, {
        headers: {
            'Authorization': `Bearer ${token()}`
        }
    });
    return response.data;
}

export const deleteCourseAsync = async (data) => {
    const response = await axios.post(`${baseUrl()}${controllerName}/DeleteCourse`, data, {
        headers: {
            'Authorization': `Bearer ${token()}`
        }
    });
    return response.data;
}

export const deletePathwayAsync = async (data) => {
    const response = await axios.post(`${baseUrl()}${controllerName}/DeletePathway`, data, {
        headers: {
            'Authorization': `Bearer ${token()}`
        }
    });
    return response.data;
}

export const removeCourseFromPathwayAsync = async (data) => {
    const response = await axios.post(`${baseUrl()}${controllerName}/RemoveCourseFromPathway`, data, {
        headers: {
            'Authorization': `Bearer ${token()}`
        }
    });
    return response.data;
}

export const generateModuleUploadSasAsync = async (data) => {
    const response = await axios.post(`${baseUrl()}course/module/generate-sas`, data, {
        headers: {
            'Authorization': `Bearer ${token()}`,
            'Content-Type': 'application/json'
        }
    });
    return getResponseData(response);
}

export const commitModuleUploadAsync = async (data) => {
    const response = await axios.post(`${baseUrl()}course/module/commit`, data, {
        headers: {
            'Authorization': `Bearer ${token()}`,
            'Content-Type': 'application/json'
        }
    });
    return getResponseData(response);
}

export const addModuleToCourseAsync = async (data, options = {}) => {
    const file = data?.get?.('file');

    if (!file) {
        throw new Error('Module file is required');
    }

    const onUploadProgress = options.onUploadProgress;
    const totalBytes = file?.size || 0;

    if (typeof onUploadProgress === 'function') {
        onUploadProgress({
            loaded: 0,
            total: totalBytes,
            phase: 'preparing'
        });
    }

    const sasResponse = await generateModuleUploadSasAsync({
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        moduleTypeId: data.get('moduleTypeId')
    });

    await uploadModuleFileToBlobAsync(file, sasResponse.sasUrl, onUploadProgress);

    return await commitModuleUploadAsync({
        blobPath: sasResponse.blobPath,
        courseId: data.get('courseId'),
        moduleTypeId: data.get('moduleTypeId'),
        title: data.get('title'),
        description: data.get('description'),
        isRequired: toBoolean(data.get('isRequired')),
        ordinal: toNullableNumber(data.get('ordinal')),
        durationMinutes: toNullableNumber(data.get('durationMinutes')),
        feedbackFormId: data.get('feedbackFormId') || null
    });
}

export const createAssessmentModuleAsync = async (data) => {
    const response = await axios.post(`${baseUrl()}assessment/CreateAssessmentModule`, data, {
        headers: {
            'Authorization': `Bearer ${token()}`,
            'Content-Type': 'application/json'
        }
    });
    return response.data.entity;
}

export const createContentModuleAsync = async (data) => {
    const response = await axios.post(`${baseUrl()}${controllerName}/AddContentModule`, data, {
        headers: {
            'Authorization': `Bearer ${token()}`,
            'Content-Type': 'application/json'
        }
    });
    return response.data;
}

export const createCourseModuleAsync = async (formData, options = {}) => {
    return addModuleToCourseAsync(formData, options);
}

export const removeCourseModuleAsync = async (data) => {
    const response = await axios.post(`${baseUrl()}${controllerName}/RemoveCourseModule`, data, {
        headers: {
            'Authorization': `Bearer ${token()}`,
            'Content-Type': 'application/json'
        }
    });
    return response.data;
}

// Group-level assignments
export const assignPathwayToGroupAsync = async (data) => {
    const response = await axios.post(`${baseUrl()}${controllerName}/AssignPathwayToGroup`, data, {
        headers: {
            'Authorization': `Bearer ${token()}`
        }
    });
    return response.data;
}

export const removePathwayFromGroupAsync = async (data) => {
    const response = await axios.post(`${baseUrl()}${controllerName}/RemovePathwayFromGroup`, data, {
        headers: {
            'Authorization': `Bearer ${token()}`
        }
    });
    return response.data;
}

export const assignCourseToGroupAsync = async (data) => {
    const response = await axios.post(`${baseUrl()}${controllerName}/AssignCourseToGroup`, data, {
        headers: {
            'Authorization': `Bearer ${token()}`
        }
    });
    return response.data;
}

export const removeCourseFromGroupAsync = async (data) => {
    const response = await axios.post(`${baseUrl()}${controllerName}/RemoveCourseFromGroup`, data, {
        headers: {
            'Authorization': `Bearer ${token()}`
        }
    });
    return response.data;
}

// Individual user-level assignments
export const assignCourseToUserAsync = async (data) => {
    const response = await axios.post(`${baseUrl()}${controllerName}/AssignCourseToUser`, data, {
        headers: {
            'Authorization': `Bearer ${token()}`
        }
    });
    return response.data;
}

export const removeCourseFromUserAsync = async (data) => {
    const response = await axios.post(`${baseUrl()}${controllerName}/RemoveCourseFromUser`, data, {
        headers: {
            'Authorization': `Bearer ${token()}`
        }
    });
    return response.data;
}

// Query available courses
export const getAvailableCoursesForUserAsync = async (userId) => {
    const response = await axios.get(`${baseUrl()}${controllerName}/GetAvailableCoursesForUser/${userId}`, {
        headers: {
            'Authorization': `Bearer ${token()}`
        }
    });
    return response.data;
}

export const getAvailableCoursesForGroupAsync = async (groupId) => {
    const response = await axios.get(`${baseUrl()}${controllerName}/GetAvailableCoursesForGroup/${groupId}`, {
        headers: {
            'Authorization': `Bearer ${token()}`
        }
    });
    return response.data;
}

export const getMyAvailableCoursesAsync = async () => {
    const response = await axios.get(`${baseUrl()}${controllerName}/GetMyAvailableCourses`, {
        headers: {
            'Authorization': `Bearer ${token()}`
        }
    });
    return response.data;
}

export const getCourseCatalogueAsync = async () => {
    const response = await axios.get(`${baseUrl()}${controllerName}/CourseCatalogue`, {
        headers: {
            'Authorization': `Bearer ${token()}`
        }
    });
    return response.data;
}